import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Draggable from 'react-draggable';
import { useDialog } from '../context/DialogContext';
import '../styles/aggregation-node-form.css';
/**
 * Below this width the form is presented as a bottom sheet rather than a
 * draggable floating panel. Keep in sync with the media query in
 * aggregation-node-form.css.
 */
const COMPACT_QUERY = '(max-width: 640px)';
function useCompactLayout() {
    const [compact, setCompact] = useState(() => typeof window !== 'undefined' && window.matchMedia(COMPACT_QUERY).matches);
    useEffect(() => {
        const mql = window.matchMedia(COMPACT_QUERY);
        const onChange = (e) => setCompact(e.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);
    return compact;
}
const AggregationNodeForm = ({ structureId, structureClass, structureTitle, structureSubtitle, existingNode, onSuccess, onCancel }) => {
    const { info } = useDialog();
    const nodeRef = useRef(null);
    const compact = useCompactLayout();
    const [loading, setLoading] = useState(false);
    const [nodeTypes, setNodeTypes] = useState([]);
    const [piaNois, setPiaNois] = useState([]);
    const [formData, setFormData] = useState({
        node_type: existingNode?.node_type || (structureClass === 'CAB' ? 'Cabinet' :
            structureClass === 'POLE' ? 'ASN' : 'CMJ'),
        node_cfn_id: existingNode?.node_cfn_id || '',
        node_bt_noi: existingNode?.node_bt_noi || '',
        node_description: existingNode?.node_description || '',
        node_status: existingNode?.node_status || 'planned',
        live_date: existingNode?.live_date ? existingNode.live_date.substring(0, 10) : '',
        structure_id: structureId
    });
    const [errors, setErrors] = useState({});
    // Load node types and structure details
    useEffect(() => {
        const loadData = async () => {
            try {
                // Load available node types
                const typesRes = await fetch('/aggregation-node/api/types');
                if (typesRes.ok) {
                    const response = await typesRes.json();
                    // API returns: { success: true, data: { types: [...] } }
                    const types = response.data?.types || response.types || [];
                    setNodeTypes(types.map((t) => ({
                        type_name: t.typeName || t.type_name,
                        display_name: t.displayName || t.display_name
                    })));
                }
                else {
                    console.error('Failed to fetch node types:', typesRes.status, typesRes.statusText);
                }
                // Load structure details for PIA NOIs
                const structRes = await fetch(`/structure/${structureId}`);
                if (structRes.ok) {
                    const struct = await structRes.json();
                    setPiaNois(struct.PiaNois || []);
                    // Auto-select NOI if only one exists
                    if (!existingNode && struct.PiaNois?.length === 1) {
                        setFormData(prev => ({ ...prev, node_bt_noi: struct.PiaNois[0].orReferenceID }));
                    }
                }
            }
            catch (error) {
                console.error('Failed to load form data:', error);
            }
        };
        loadData();
    }, [structureId, existingNode]);
    // Close on Escape
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && !loading)
                onCancel();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [loading, onCancel]);
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };
    const handleNodeTypeChange = (e) => {
        const nodeType = e.target.value;
        setFormData(prev => ({ ...prev, node_type: nodeType }));
        // Clear CFN ID error when changing type
        if (errors.node_cfn_id) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.node_cfn_id;
                return newErrors;
            });
        }
    };
    const requiresCfnId = () => {
        return !['Jacket', 'VIRT'].includes(formData.node_type);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});
        try {
            // Clean up data
            const submitData = { ...formData };
            if (submitData.node_cfn_id === '')
                submitData.node_cfn_id = null;
            if (submitData.node_bt_noi === '')
                submitData.node_bt_noi = null;
            if (submitData.live_date === '')
                submitData.live_date = null;
            const url = existingNode
                ? `/agg_node/${existingNode.id}`
                : '/agg_node';
            const method = existingNode ? 'PUT' : 'POST';
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(submitData)
            });
            const result = await response.json();
            if (response.ok) {
                await info({
                    message: existingNode
                        ? 'Aggregation node updated successfully'
                        : 'Aggregation node created successfully',
                    variant: 'success'
                });
                onSuccess();
            }
            else if (result.errors) {
                setErrors(result.errors);
            }
            else {
                throw new Error(result.error || 'Failed to save aggregation node');
            }
        }
        catch (error) {
            await info({
                title: 'Save Error',
                message: error instanceof Error ? error.message : 'Failed to save aggregation node',
                variant: 'error'
            });
        }
        finally {
            setLoading(false);
        }
    };
    const fieldError = (name) => errors[name] ? (_jsx("div", { className: "agg-node-form__error", children: errors[name].join(', ') })) : null;
    const title = existingNode ? 'Edit Aggregation Node' : 'Add Aggregation Node';
    const panel = (_jsxs("div", { ref: nodeRef, className: `agg-node-form ${compact ? 'agg-node-form--sheet' : 'agg-node-form--panel'}`, role: "dialog", "aria-modal": "true", "aria-labelledby": "agg-node-form-title", children: [_jsxs("div", { className: "agg-node-form__header", children: [compact && _jsx("div", { className: "agg-node-form__grabber", "aria-hidden": "true" }), _jsxs("div", { className: "agg-node-form__heading", children: [_jsxs("h3", { id: "agg-node-form-title", className: "agg-node-form__title", children: [!compact && _jsx("i", { className: "fas fa-grip-vertical agg-node-form__drag-handle" }), title] }), (structureTitle || structureSubtitle) && (_jsxs("div", { className: "agg-node-form__structure", title: "Structure this node belongs to", children: [_jsx("span", { className: "agg-node-form__structure-label", children: "Structure" }), structureTitle && _jsx("span", { className: "agg-node-form__structure-id", children: structureTitle }), structureSubtitle && _jsx("span", { className: "agg-node-form__structure-ref", children: structureSubtitle })] }))] }), _jsx("button", { type: "button", className: "agg-node-form__close", onClick: onCancel, disabled: loading, "aria-label": "Close", children: _jsx("i", { className: "fas fa-times" }) })] }), _jsxs("form", { onSubmit: handleSubmit, className: "agg-node-form__form", children: [_jsx("div", { className: "agg-node-form__body", children: _jsxs("div", { className: "agg-node-form__grid", children: [_jsxs("div", { className: `agg-node-form__field ${requiresCfnId() ? '' : 'agg-node-form__field--full'}`, children: [_jsxs("label", { className: "agg-node-form__label", htmlFor: "node_type", children: ["Node Type ", _jsx("span", { className: "agg-node-form__required", children: "*" })] }), _jsx("select", { id: "node_type", name: "node_type", value: formData.node_type, onChange: handleNodeTypeChange, required: true, disabled: structureClass === 'CAB' && !existingNode, className: `agg-node-form__select ${errors.node_type ? 'agg-node-form__input--error' : ''}`, children: nodeTypes
                                                .filter(type => {
                                                // Filter based on structure class
                                                if (['JB', 'MH'].includes(structureClass)) {
                                                    return type.type_name !== 'ASN';
                                                }
                                                if (structureClass === 'POLE') {
                                                    return type.type_name !== 'Jacket';
                                                }
                                                return true;
                                            })
                                                .map(type => (_jsx("option", { value: type.type_name, children: type.display_name }, type.type_name))) }), fieldError('node_type')] }), requiresCfnId() && (_jsxs("div", { className: "agg-node-form__field", children: [_jsxs("label", { className: "agg-node-form__label", htmlFor: "node_cfn_id", children: ["CFN ID ", _jsx("span", { className: "agg-node-form__required", children: "*" })] }), _jsx("input", { type: "text", id: "node_cfn_id", name: "node_cfn_id", value: formData.node_cfn_id, onChange: handleChange, placeholder: "CAM-AAN-AUTO", pattern: "^[A-Z]{3}-(AAN|FSP)-(|AUTO|[0-9]{5})$", required: requiresCfnId(), autoComplete: "off", autoCapitalize: "characters", "data-lpignore": "true", className: `agg-node-form__input ${errors.node_cfn_id ? 'agg-node-form__input--error' : ''}` }), _jsx("div", { className: "agg-node-form__hint", children: "XXX-AAN-AUTO or XXX-AAN-00000" }), fieldError('node_cfn_id')] })), _jsxs("div", { className: `agg-node-form__field ${formData.node_status === 'installed' ? '' : 'agg-node-form__field--full'}`, children: [_jsxs("label", { className: "agg-node-form__label", htmlFor: "node_status", children: ["Status ", _jsx("span", { className: "agg-node-form__required", children: "*" })] }), _jsxs("select", { id: "node_status", name: "node_status", value: formData.node_status, onChange: handleChange, required: true, className: `agg-node-form__select ${errors.node_status ? 'agg-node-form__input--error' : ''}`, children: [_jsx("option", { value: "planned", children: "Planned" }), _jsx("option", { value: "installed", children: "Installed" }), _jsx("option", { value: "removed", children: "Removed" })] }), fieldError('node_status')] }), formData.node_status === 'installed' && (_jsxs("div", { className: "agg-node-form__field", children: [_jsxs("label", { className: "agg-node-form__label", htmlFor: "live_date", children: ["Live Date ", _jsx("span", { className: "agg-node-form__required", children: "*" })] }), _jsx("input", { type: "date", id: "live_date", name: "live_date", value: formData.live_date, onChange: handleChange, required: formData.node_status === 'installed', className: `agg-node-form__input ${errors.live_date ? 'agg-node-form__input--error' : ''}` }), fieldError('live_date')] })), _jsxs("div", { className: "agg-node-form__field agg-node-form__field--full", children: [_jsx("label", { className: "agg-node-form__label", htmlFor: "node_bt_noi", children: "PIA NOI" }), _jsxs("select", { id: "node_bt_noi", name: "node_bt_noi", value: formData.node_bt_noi, onChange: handleChange, className: `agg-node-form__select ${errors.node_bt_noi ? 'agg-node-form__input--error' : ''}`, children: [_jsx("option", { value: "", children: "None" }), piaNois.map(noi => (_jsx("option", { value: noi.orReferenceID, children: noi.orReferenceID }, noi.orReferenceID)))] }), fieldError('node_bt_noi')] }), _jsxs("div", { className: "agg-node-form__field agg-node-form__field--full", children: [_jsxs("label", { className: "agg-node-form__label", htmlFor: "node_description", children: ["Description ", _jsx("span", { className: "agg-node-form__required", children: "*" })] }), _jsx("textarea", { id: "node_description", name: "node_description", value: formData.node_description, onChange: handleChange, required: true, rows: 3, className: `agg-node-form__textarea ${errors.node_description ? 'agg-node-form__input--error' : ''}` }), fieldError('node_description')] })] }) }), _jsxs("div", { className: "agg-node-form__buttons", children: [_jsx("button", { type: "button", onClick: onCancel, disabled: loading, className: "agg-node-form__button agg-node-form__button--secondary", children: "Cancel" }), _jsx("button", { type: "submit", disabled: loading, className: "agg-node-form__button agg-node-form__button--primary", children: loading ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "agg-node-form__spinner" }), existingNode ? 'Updating...' : 'Creating...'] })) : (existingNode ? 'Update Node' : 'Create Node') })] })] })] }));
    // Portal to <body>: the structure popup lives inside a Leaflet popup, which
    // is transformed and overflow-clipped, so a position:fixed overlay rendered
    // in place is trapped inside the popup box rather than covering the viewport.
    return createPortal(_jsx("div", { className: "agg-node-form__overlay", children: compact ? (panel) : (_jsx(Draggable, { handle: ".agg-node-form__header", defaultPosition: { x: 0, y: 0 }, cancel: "input, textarea, select, button", nodeRef: nodeRef, children: panel })) }), document.body);
};
export default AggregationNodeForm;
